/**
 * ProductAddPage — mirrors Angular `ProductAddComponent` (policies, dirty gate, POST then navigate to edit).
 * UI matches product module add-product screenshots: page header actions + single form card.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { ProductAddFormValues } from '../types'
import { productAddFormSchema } from '../types/schemas'
import { Loader2, AlertCircle } from 'lucide-react'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { CKEditor4 } from '@/components/editor/CKEditor4'
import { useUserRole } from '@/hooks/useUserRole'
import { showSuccess, showProductApiError } from '@/lib/utils/notifications'
import { DESCRIPTION_MAX, PRODUCT_ADD_INITIAL_VALUES, PRODUCT_POLICIES } from '../constants'
import { createProduct } from '../services/productApi'
import { cn } from '@/lib/utils/helpers'
import styles from '../styles/productAdd.module.css'

export function ProductAddPage() {
  const navigate = useNavigate()
  const { hasPolicy } = useUserRole()
  const canAdd = hasPolicy([PRODUCT_POLICIES.ADD])

  const [baseline] = useState(() => ({ ...PRODUCT_ADD_INITIAL_VALUES }))
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<ProductAddFormValues>({
    resolver: zodResolver(productAddFormSchema),
    defaultValues: PRODUCT_ADD_INITIAL_VALUES,
    mode: 'onChange',
  })

  const { watch, handleSubmit, control, formState, register } = form
  const values = watch()
  const descriptionLen = (values.description ?? '').length

  const dirty = useMemo(() => {
    return (
      values.name !== baseline.name ||
      values.description !== baseline.description ||
      values.long_description !== baseline.long_description ||
      values.allow_dynamic_access_fee !== baseline.allow_dynamic_access_fee ||
      values.is_active !== baseline.is_active
    )
  }, [values, baseline])

  useEffect(() => {
    if (!canAdd) {
      navigate('/product', { replace: true })
    }
  }, [canAdd, navigate])

  if (!canAdd) return null

  const onSubmit = handleSubmit(async (data) => {
    // Angular `submitForm()` early-returns when the form is not dirty (in addition to the disabled button).
    if (!dirty) return
    setSubmitting(true)
    try {
      // Angular sends name/description raw (untrimmed) — mirror that here.
      const res = await createProduct({
        name: data.name,
        description: data.description ?? '',
        long_description: data.long_description ?? '',
        allow_dynamic_access_fee: data.allow_dynamic_access_fee ? 1 : 0,
        is_active: data.is_active ? 1 : 0,
      })
      if (res.product_id && res.product_id > 0) {
        showSuccess('Product created successfully!')
        navigate(`/product/edit/${res.product_id}`)
      } else {
        showProductApiError('Failed to create product')
      }
    } catch (e) {
      showProductApiError(e)
    } finally {
      setSubmitting(false)
    }
  },
  () => {
    // Angular scrollToFirstError: center the first invalid field on invalid submit.
    document.getElementById('product_name')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  },
  )

  return (
    <div className="w-full min-h-screen bg-slate-50/50" data-testid="product-add-page">
      <PageHeader title="Add Product" testIdRoot="product-add-page-header" />

      <div className="mx-4 my-4 sm:mx-6 sm:my-5">
        <div className="mb-5 flex flex-col gap-4 sm:mb-6 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900">
              Add Product
            </h1>
            <p className="mt-1 mb-0 text-sm text-slate-500">
              Create and configure a new product.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2 sm:gap-3">
            <Link
              to="/product"
              className={styles.btnSecondary}
              data-testid="product-add-cancel"
            >
              Cancel
            </Link>
            <button
              type="button"
              disabled={submitting || !dirty}
              className={styles.btnPrimary}
              data-testid="product-add-submit"
              onClick={() => void onSubmit()}
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Submit
            </button>
          </div>
        </div>

        <form
          className={styles.formCard}
          data-testid="product-add-form"
          onKeyDown={(e) => {
            // Angular add: (keydown.enter)="$event.preventDefault()" — Enter in a single-line field must not submit.
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
              e.preventDefault()
            }
          }}
          onSubmit={(e) => {
            e.preventDefault()
            void onSubmit()
          }}
        >
          {/* Product information */}
          <div className={styles.sectionBlock} data-testid="product-add-section-basic-info">
            <div>
              <h2 className={styles.sectionTitle} data-testid="product-add-section-basic-info-title">
                Product information
              </h2>
              <p className={styles.sectionSubtitle}>
                Enter the product details shown to users and administrators.
              </p>
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="product_name" className={styles.formLabel}>
                Product name<span className={styles.requiredStar}>*</span>
              </label>
              <input
                id="product_name"
                type="text"
                className={cn(
                  styles.customFormControl,
                  formState.errors.name && styles.customFormControlError,
                )}
                {...register('name')}
                placeholder="Enter product name"
                data-testid="product-add-name"
              />
              {formState.errors.name && (
                <div className={styles.errorMessage}>
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {formState.errors.name.message}
                </div>
              )}
            </div>

            <div className={styles.formGroup}>
              <label htmlFor="product_description" className={styles.formLabel}>
                Short description
              </label>
              <textarea
                id="product_description"
                rows={4}
                maxLength={DESCRIPTION_MAX}
                className={styles.customFormControl}
                {...register('description')}
                placeholder="Enter product description"
                data-testid="product-add-description"
              />
              <div className={styles.charCounter} aria-live="polite">
                {descriptionLen} / {DESCRIPTION_MAX}
              </div>
            </div>

            <div className={styles.formGroup}>
              <span className={styles.formLabel}>Long description</span>
              <Controller
                name="long_description"
                control={control}
                render={({ field }) => (
                  <CKEditor4
                    id="product-add-long-desc"
                    value={field.value || ''}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>

          <hr className={styles.sectionDivider} />

          {/* Configuration */}
          <div className={styles.sectionBlock} data-testid="product-add-section-settings">
            <div>
              <h2 className={styles.sectionTitle} data-testid="product-add-section-settings-title">
                Configuration
              </h2>
            </div>

            <div className="flex flex-col gap-3">
              <Controller
                name="allow_dynamic_access_fee"
                control={control}
                render={({ field }) => (
                  <div className={styles.configRow}>
                    <div className={styles.configRowText}>
                      <p className={styles.configRowTitle}>Dynamic access fee</p>
                      <p className={styles.configRowDesc}>
                        Allow the access fee to be configured dynamically.
                      </p>
                    </div>
                    <label className={styles.toggleLabel}>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={field.value}
                        onClick={() => field.onChange(!field.value)}
                        className={cn(
                          styles.toggleTrack,
                          field.value ? styles.toggleTrackOn : styles.toggleTrackOff,
                        )}
                        data-testid="product-add-allow-dynamic-access-fee-toggle"
                      >
                        <span
                          className={cn(
                            styles.toggleThumb,
                            field.value ? styles.toggleThumbOn : styles.toggleThumbOff,
                          )}
                        />
                      </button>
                      <span className={cn(styles.toggleText, field.value && styles.toggleTextOn)}>
                        {field.value ? 'On' : 'Off'}
                      </span>
                    </label>
                  </div>
                )}
              />

              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <div className={styles.configRow}>
                    <div className={styles.configRowText}>
                      <p className={styles.configRowTitle}>Product status</p>
                      <p className={styles.configRowDesc}>
                        Make this product available for use.
                      </p>
                    </div>
                    <label className={styles.toggleLabel}>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={field.value}
                        onClick={() => field.onChange(!field.value)}
                        className={cn(
                          styles.toggleTrack,
                          field.value ? styles.toggleTrackOnGreen : styles.toggleTrackOff,
                        )}
                        data-testid="product-add-status-toggle"
                      >
                        <span
                          className={cn(
                            styles.toggleThumb,
                            field.value ? styles.toggleThumbOn : styles.toggleThumbOff,
                          )}
                        />
                      </button>
                      <span className={cn(styles.toggleText, field.value && styles.toggleTextOn)}>
                        {field.value ? 'Active' : 'Inactive'}
                      </span>
                    </label>
                  </div>
                )}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
